<script lang="ts">
	/**
	 * The one control for choosing a country, everywhere the store asks for one.
	 *
	 * It replaced a two-letter text input on four screens and a plain 249-entry `<select>` on a
	 * fifth. Both are worse than they look. A text field asks a customer at checkout to know that
	 * Germany is `DE` and Sweden is `SE` — and it accepts `UK`, which is not a country code, and
	 * `EN`, which is Ireland's neighbour spelt from memory; the tax table then answers with a
	 * refusal about a country nobody chose. A bare `<select>` fixes the vocabulary but not the
	 * finding: scrolling to Switzerland past two hundred entries is the reason people abandon
	 * checkout forms.
	 *
	 * So: a text box that filters, over a listbox that is the authority on what may be picked.
	 * **The typing never becomes the value** — only an option can, which is what keeps `UK` out
	 * while still letting somebody type "unit" and be offered both Kingdom and States.
	 *
	 * Names come from `Intl.DisplayNames` in the reader's own language, and the filter matches the
	 * name *and* the code with diacritics stripped: a customer types "Aland", an admin entering a
	 * tax rate types "DE", and both are the same control.
	 *
	 * Accessibility is the ARIA combobox pattern rather than a div that looks like one — `role`,
	 * `aria-expanded`, `aria-activedescendant` and arrow/Enter/Escape keys — because a control that
	 * gates a purchase must not be reachable only by mouse.
	 */

	import { createEventDispatcher, tick } from 'svelte';
	import { anchoredPopup } from './anchoredPopup';
	import { countryName, countryOptions, filterCountries } from '../Countries';
	import type { CountryOption } from '../Countries';

	/** The ISO-3166 alpha-2 code, uppercase. `''` is "nothing chosen". Bindable. */
	export let value: string = '';
	/** `for`/`id` wiring — pass the same value as the enclosing `FormField`'s `id`. */
	export let id: string = '';
	export let disabled: boolean = false;
	/**
	 * Announced to assistive technology, **not** enforced by the browser.
	 *
	 * A native `required` on this input would have the browser refuse the form with "Please fill out
	 * this field" — a message about a text box, on a control that is not one, in place of the
	 * screen's own sentence about which country it needs. Every form here already decides its own
	 * submittability in JavaScript (`canSubmit`, `canSave`), so this is the label and that is the
	 * gate.
	 */
	export let required: boolean = false;
	export let placeholder: string = 'Search for a country…';
	/**
	 * Offers a "no country" row. Off by default: most of these fields are required, and a clear
	 * control on one that is not clearable is a button that does nothing.
	 */
	export let allowEmpty: boolean = false;
	/** The label of the empty row, since "none" means something different on each screen. */
	export let emptyLabel: string = 'No country';
	/**
	 * Overrides the language names are rendered in. Only set by tests — left unset it follows the
	 * reader's own locale, which is the whole point of naming through `Intl`.
	 */
	export let locale: string | undefined = undefined;

	let cssClass: string = '';
	export { cssClass as class };

	const dispatcher = createEventDispatcher<{ change: { value: string } }>();

	const options: CountryOption[] = countryOptions(locale);

	let open: boolean = false;
	let query: string = '';
	let activeIndex: number = 0;
	let input: HTMLInputElement | undefined;
	let list: HTMLUListElement | undefined;
	/** The control the list is placed against. See `anchoredPopup` for why it is not a child. */
	let wrapper: HTMLDivElement | undefined;
	/**
	 * Held across the one `focus()` call that follows a choice.
	 *
	 * The control opens on focus and `fnChoose` puts focus back on the field, so without this,
	 * picking a country immediately reopens the list it has just answered — with an empty query, so
	 * the box reads blank while the value behind it is set. In a browser the input never lost focus
	 * in the first place (the option's `mousedown` is defaulted away for exactly that reason) and
	 * the call is a no-op; it is every path where focus *did* move that needs this.
	 */
	let reopenSuppressed: boolean = false;

	/** The rows on offer: the countries the query matches, plus the clear row when there is one. */
	$: matches = filterCountries(options, query);
	$: rows = allowEmpty ? [{ value: '', name: emptyLabel }, ...matches] : matches;

	/**
	 * What the closed control reads.
	 *
	 * Resolved through `countryName` rather than by looking `value` up in `options`, so a code the
	 * list does not carry — a legacy row, a code typed by an older version of this form — still
	 * renders as *something* instead of as an empty box that looks unset.
	 */
	$: selectedName = value === '' ? '' : countryName(value, locale);

	// The box shows the query while it is being typed and the selection the rest of the time, so a
	// half-typed string never sits there looking like an answer.
	$: display = open ? query : selectedName;

	function fnOpen(): void {
		if (disabled || open || reopenSuppressed) return;
		open = true;
		query = '';
		activeIndex = Math.max(
			0,
			rows.findIndex(row => row.value === value)
		);
		void fnScrollActiveIntoView();
	}

	function fnClose(): void {
		open = false;
		query = '';
	}

	function fnChoose(code: string): void {
		value = code;
		dispatcher('change', { value: code });
		fnClose();

		// Focus stays on the field so the keyboard path continues where it was, and the reopen that
		// would otherwise cause is suppressed across the call. See `reopenSuppressed`.
		reopenSuppressed = true;
		input?.focus();
		reopenSuppressed = false;
	}

	function fnInput(event: Event): void {
		query = (event.target as HTMLInputElement).value;
		open = true;
		// The list is a different list now, so an index into the old one means nothing.
		activeIndex = 0;
	}

	async function fnScrollActiveIntoView(): Promise<void> {
		await tick();
		// `block: 'nearest'` so the list only moves when the active row is actually off-screen —
		// centring on every keystroke makes the whole list jump under a reader's eye.
		list?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
	}

	function fnKeyDown(event: KeyboardEvent): void {
		if (disabled) return;

		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			if (!open) {
				fnOpen();
				return;
			}
			if (rows.length === 0) return;
			const step = event.key === 'ArrowDown' ? 1 : -1;
			activeIndex = (activeIndex + step + rows.length) % rows.length;
			void fnScrollActiveIntoView();
			return;
		}

		if (event.key === 'Enter') {
			// Only while the list is open, so Enter in a closed control still submits the form it
			// sits in rather than being swallowed by a picker that is not doing anything.
			if (!open) return;
			event.preventDefault();
			const row = rows[activeIndex];
			if (row !== undefined) {
				fnChoose(row.value);
			}
			return;
		}

		if (event.key === 'Escape') {
			if (!open) return;
			// Stopped as well as defaulted: this control is used inside modals, and an Escape that
			// bubbles closes the whole dialog when the user meant to close the list.
			event.preventDefault();
			event.stopPropagation();
			fnClose();
			return;
		}

		if (event.key === 'Tab' && open) {
			fnClose();
		}
	}
</script>

<div class="relative {cssClass}" bind:this={wrapper}>
	<div class="relative">
		<input
			bind:this={input}
			{id}
			{disabled}
			type="text"
			role="combobox"
			aria-required={required ? 'true' : undefined}
			autocomplete="off"
			aria-expanded={open}
			aria-controls={`${id}-listbox`}
			aria-activedescendant={open && rows[activeIndex] !== undefined
				? `${id}-option-${rows[activeIndex].value || 'none'}`
				: undefined}
			class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 pr-9 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white dark:placeholder-zinc-400"
			placeholder={value === '' || open ? placeholder : ''}
			value={display}
			on:input={fnInput}
			on:focus={fnOpen}
			on:keydown={fnKeyDown}
			on:blur={fnClose}
		/>
		<!--
			Decoration only, and `aria-hidden` for that reason: the input above is the combobox, and
			a second focusable thing here would put a tab stop between the field and its list.
		-->
		<i
			class="fa-solid fa-chevron-{open ? 'up' : 'down'} pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs opacity-60"
			aria-hidden="true"
		/>
	</div>

	{#if open}
		<!--
			Positioned by `anchoredPopup`, which moves it to `document.body`. This picker is on five
			screens and four of them are modals, where an absolutely-positioned list counts toward
			the dialog's own scroll height — so opening it makes the modal scroll under the reader.
			Nothing about focus changes here: the input closes on `blur` and options commit on
			`mousedown`, so focus never enters the list wherever the list happens to live.
		-->
		<ul
			bind:this={list}
			use:anchoredPopup={{ anchor: wrapper }}
			id={`${id}-listbox`}
			role="listbox"
			class="widget bg-depth max-h-64 overflow-y-auto p-1 shadow-widget border-widget"
		>
			{#if rows.length === 0}
				<!-- Said rather than shown as an empty box: a picker with nothing in it and no words
				     reads as broken, when the true answer is that the query matched nothing. -->
				<li class="px-3 py-2 text-sm text-default">No country matches “{query}”.</li>
			{:else}
				{#each rows as row, index (row.value)}
					<li
						id={`${id}-option-${row.value || 'none'}`}
						role="option"
						aria-selected={row.value === value}
						data-active={index === activeIndex}
						class="cursor-pointer rounded-lg px-3 py-1.5 text-sm {index === activeIndex
							? 'bg-primary-100 dark:bg-primary-900/40'
							: ''}"
						on:mouseenter={() => (activeIndex = index)}
						on:mousedown|preventDefault={() => fnChoose(row.value)}
					>
						<span class="{row.value === '' ? 'text-default italic' : ''}">{row.name}</span>
						{#if row.value !== ''}
							<span class="ml-2 font-mono text-xs text-default">{row.value}</span>
						{/if}
					</li>
				{/each}
			{/if}
		</ul>
	{/if}
</div>
